import React, { useState, useEffect, useContext } from 'react';
import useToastContext from 'hooks/useToastContext';
import { useForm } from 'react-hook-form';
import { apiPost } from '../../utils/apiFetch';
import apiPath from '../../utils/apiPath';
import { useTranslation } from 'react-i18next';
import OInputField from 'components/reusable/OInputField';
import formValidation from 'utils/formValidation';
import { useNavigate } from 'react-router-dom';
import AuthContext from 'context/AuthContext';
import ErrorMessage from 'components/ErrorMessage';
import { Editor } from '@tinymce/tinymce-react';
import { isEmpty } from 'lodash';
import OImage from 'components/reusable/OImage';
import noImageFound from '../../assets/images/No-image-found.jpg';

const AddStaticContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    shouldFocusError: true
  });
  const notification = useToastContext();
  const [content, setContent] = useState('');
  const [photo, setPhoto] = useState('');
  const [photoError] = useState('');
  const { updatePageName } = useContext(AuthContext);

  useEffect(() => {
    updatePageName(t('ADD_STATIC_CONTENT'));
  }, []);

  const onSubmit = async data => {
    try {
      if (!isEmpty(content)) {
        const formData = new FormData();
        formData.append('title', data?.title?.trim());
        formData.append('metaTitle', data?.metaTitle);
        formData.append('metaDescription', data?.metaDescription);
        formData.append('keyword', data?.keyword);
        formData.append('description', content);
        formData.append('StaticContentImage', photo);
        const res = await apiPost(apiPath.getStaticContent, formData);
        if (res?.data?.success) {
          navigate('/static-page-management');
          notification.success(res.data?.message);
        } else {
          notification.error(res?.data?.message);
        }
      } else {
        notification.error('Please enter description');
      }
    } catch (err) {
      console.log('err:', err);
    }
  };

  return (
    <>
      <div>
        <div className='bg-[#F9F9F9]'>
          <div className='px-3 py-4'>
            <div className='bg-white border border-[#E9EDF9] rounded-lg'>
              <div className='border-b-[#E3E3E3] grid 2xl:grid-cols-3 xl:grid-cols-2 lg:grid lg:grid-cols-1 gap-2 px-4'>
                <div className='col-span-3 flex flex-wrap items-center' />
              </div>

              <div className='flex pl-5 pt-4'>
                <strong>{t('O_ADMIN_BLOG_IMAGE_SIZE_SUGGESTION')}</strong>
              </div>

              <div className='pl-5 pt-4 flex'>
                <label className='mb-2 block pr-1'>
                  <strong>{t('UPLOAD_IMAGE')}</strong>
                </label>
                <input
                  type='file'
                  name='photo'
                  placeholder='Photo'
                  className='form-control'
                  accept='image/png,image/jpeg,image/jpg'
                  onChange={event => {
                    setPhoto(event?.target?.files[0]);
                  }}
                />
              </div>

              {photoError && <ErrorMessage message={photoError} />}

              <div className='pl-5 pt-4'>
                <OImage
                  src={
                    typeof photo === 'object'
                      ? URL.createObjectURL(photo)
                      : photo
                  }
                  fallbackUrl={noImageFound}
                />
              </div>

              <div className='p-5 shadow-md'>
                <OInputField
                  wrapperClassName='relative z-0 mb-6 w-full group'
                  name='title'
                  inputLabel={
                    <>
                      {t('TITLE')}
                      <span className='text-red-500'>*</span>
                    </>
                  }
                  type='text'
                  autoFocus
                  register={register('title', formValidation['title'])}
                  errors={errors}
                />

                <OInputField
                  wrapperClassName='relative z-0 mb-6 w-full group'
                  name='metaTitle'
                  inputLabel={<>{t('META_TITLE')}</>}
                  type='text'
                  register={register('metaTitle', formValidation['metaTitle'])}
                />

                <OInputField
                  wrapperClassName='relative z-0 mb-6 w-full group'
                  name='metaDescription'
                  inputLabel={<>{t('META_DESCRIPTION')}</>}
                  type='text'
                  register={register(
                    'metaDescription',
                    formValidation['metaDescription']
                  )}
                />

                <OInputField
                  wrapperClassName='relative z-0 mb-6 w-full group'
                  name='keyword'
                  inputLabel={<>{t('KEYWORD')}</>}
                  type='text'
                  register={register('keyword', formValidation['keyword'])}
                />

                <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '10px' }}>

                  <Editor
                    apiKey="mkzldhxz903im4nozx302n82dfa451insn0l8g423ay9x8s0"
                    value={content}
                    onEditorChange={(newValue) => setContent(newValue)}
                    init={{
                      height: 500,
                      menubar: 'file edit view insert format tools help',
                      plugins: [
                        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                        'insertdatetime', 'media', 'table', 'paste', 'help', 'wordcount'
                      ],
                      toolbar:
                        'undo redo | formatselect | bold italic backcolor | ' +
                        'alignleft aligncenter alignright alignjustify | ' +
                        'bullist numlist outdent indent | removeformat | code | help',
                      content_style:
                        'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
                    }}
                  />


                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='border-slate-200 rounded-bflex items-center justify-center p-6 border-t border-solid'>
        <button
          className='rounded mr-6 ease-linear transition-all text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none duration-150'
          onClick={() => navigate('/static-page-management')}
          type='button'
        >
          {t('O_BACK')}
        </button>

        <button
          onClick={handleSubmit(onSubmit)}
          className='outline-none bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg focus:outline-none mr-1 ease-linear transition-all duration-150'
          type='submit'
        >
          {t('O_ADD')}
        </button>
      </div>
    </>
  );
};

export default AddStaticContent;
